export const content = {
  en: {
    brand: 'FlyHigh Brothers',
    tagline: "Egypt's Premier Aerial Advertising & Media Solutions Company",
    nav: {
      home: 'Home',
      about: 'About',
      aerial: 'Aerial Advertising',
      digital: 'Digital Marketing',
      production: 'Media Production',
      web: 'Web Development',
      story: 'Our Story',
      news: 'News',
      contact: 'Get in Touch',
    },
    hero: {
      eyebrow: "EGYPT'S PREMIER AERIAL MEDIA SOLUTIONS",
      titleLine1: 'WHERE BRANDS',
      titleLine2: 'FLY HIGH',
      subtitle:
        'Own the Sky. Own the Screen. Experience a new dimension of advertising where your message is impossible to ignore.',
      ctaPrimary: 'EXPLORE AERIAL SOLUTIONS',
      ctaSecondary: 'WATCH SHOWREEL',
    },
    problem: {
      eyebrow: 'THE CHALLENGE',
      title: 'YOUR AUDIENCE IS DROWNING IN ADS',
      subtitle: "AND THEY'VE STOPPED LISTENING",
      description:
        'The average Egyptian consumer is exposed to over 5,000 advertising messages per day. Most are ignored. Scrolled past. Blocked. Forgotten.',
      cards: [
        {
          title: 'Banner Blindness',
          description:
            'Digital ads are ignored at record rates. CTRs have fallen below 0.1% on most platforms. Your budget is disappearing into the void.',
        },
        {
          title: 'Outdoor Saturation',
          description:
            'Every billboard, every wall, every bus is already taken. Your brand blends in with the noise instead of standing out.',
        },
        {
          title: 'Zero Memorability',
          description:
            'When everyone shouts the same way, no one is heard. Brands need a new dimension — one that cannot be replicated or ignored.',
        },
      ],
      bridge: 'There is only one medium left that cannot be blocked, skipped, or ignored →',
    },
    solution: {
      eyebrow: 'THE SOLUTION',
      title: 'THE SKY IS THE LAST',
      titleAccent: 'UNCLUTTERED MEDIUM',
      description:
        'When your brand appears in the sky above a crowd of 50,000 people, there is no scroll button. No ad blocker. No competing message. Just your brand — at scale, in motion, impossible to miss.',
      stats: [
        { value: '100%', label: 'VISIBILITY' },
        { value: '0', label: 'AD BLOCKERS' },
        { value: '50K+', label: 'SIMULTANEOUS IMPRESSIONS' },
      ],
    },
    whyUs: {
      eyebrow: 'WHY US',
      title: 'THE FLY HIGH DIFFERENCE',
      items: [
        {
          title: 'EXCLUSIVE ACCESS',
          description:
            "We operate Egypt's only dedicated aerial advertising fleet. No other agency can offer what we offer — period.",
        },
        {
          title: 'UNMATCHED VISIBILITY',
          description:
            'Sky-level exposure reaches audiences of tens of thousands simultaneously — with zero ad-blocking and 100% viewability.',
        },
        {
          title: 'PREMIUM POSITIONING',
          description:
            'Our services are designed for brands that demand the highest tier of execution, presentation, and results.',
        },
        {
          title: 'FULL-SERVICE ECOSYSTEM',
          description:
            'From the sky to the screen, we handle aerial, digital, production, and development — all under one roof.',
        },
      ],
    },
    services: {
      eyebrow: 'WHAT WE DO',
      title: 'OWN THE SKY',
      titleAccent: 'OWN THE SCREEN',
      subtitle:
        'A complete ecosystem of aerial and media solutions — designed to make your brand IMPOSSIBLE TO IGNORE.',
      items: [
        {
          title: 'Aerial Advertising',
          path: '/aerial-advertising',
        },
        {
          title: 'Marketing & Digital',
          path: '/digital-marketing',
        },
        {
          title: 'Media Production',
          path: '/media-production',
        },
        {
          title: 'Web & App Development',
          path: '/web-development',
        },
      ],
    },
    trustedBy: {
      title: 'TRUSTED BY BRANDS THAT FLY WITH US',
      industries: ['REAL ESTATE', 'F&B', 'TECH', 'FASHION', 'EVENTS', 'GOVERNMENT'],
    },
    flyPartners: {
      eyebrow: 'TRUSTED BY',
      title: 'BRANDS THAT FLY WITH US',
      description:
        "We are proud to have partnered with some of Egypt's most respected brands across real estate, technology, food & beverage, fashion, and more.",
    },
    commercialPartners: {
      eyebrow: 'COMMERCIALS',
      title: 'BRANDS THAT TRUST THE LENS',
      subtitle: 'COMMERCIAL PRODUCTION CREDITS',
      description:
        'Our producer, Ahmed Ibrahim, has produced commercial campaigns for some of the most recognized brands in Egypt and the Arab world — delivering high-impact, broadcast-quality advertising content.',
    },
    impact: {
      eyebrow: 'OUR IMPACT',
      title: 'NUMBERS THAT SPEAK',
      stats: [
        { value: '50+', label: 'Successful Campaigns Executed' },
        { value: '100M+', label: 'Audience Impressions Delivered' },
        { value: '20+', label: 'Brands Served Across Industries' },
        { value: '100%', label: 'Campaign Completion Rate' },
      ],
    },
    testimonials: {
      eyebrow: 'SOCIAL PROOF',
      title: 'WHAT OUR CLIENTS SAY',
      items: [
        {
          quote:
            'The aerial banner campaign generated more buzz than our entire digital spend that month. People were posting about it for days.',
          role: 'Marketing Director',
          company: 'Real Estate Developer',
        },
        {
          quote:
            'FlyHigh Brothers delivered a gift-dropping activation that our audience is still talking about six months later. Truly unforgettable.',
          role: 'Brand Manager',
          company: 'FMCG Company',
        },
        {
          quote:
            'Their media production team captured our brand story in a way we never imagined possible. The aerial footage was breathtaking.',
          role: 'CEO',
          company: 'Fashion & Retail Brand',
        },
        {
          quote:
            'The combination of aerial advertising and digital amplification gave us a reach we could never have achieved through traditional channels.',
          role: 'Head of Marketing',
          company: 'Technology Company',
        },
      ],
    },
    howWeWork: {
      eyebrow: 'HOW WE WORK',
      title: 'FROM BRIEF TO LAUNCH',
      steps: [
        {
          number: '01',
          title: 'BRIEF',
          description:
            'We start with a deep-dive into your brand, your audience, and your campaign objectives. No assumptions. Just clarity.',
        },
        {
          number: '02',
          title: 'STRATEGY',
          description:
            'Our team develops a tailored campaign concept with a clear execution plan, timeline, and measurable KPIs.',
        },
        {
          number: '03',
          title: 'EXECUTION',
          description:
            'We deploy your campaign with precision — on time, on budget, and to the highest standard of quality.',
        },
        {
          number: '04',
          title: 'RESULTS',
          description:
            'We report on performance, share insights, and optimize for your next campaign.',
        },
      ],
    },
    opportunity: {
      eyebrow: 'THE OPPORTUNITY',
      title: 'THE WINDOW IS OPEN',
      titleAccent: 'FOR NOW',
      items: [
        {
          title: 'FIRST-MOVER ADVANTAGE',
          description:
            'Your competitors have not yet discovered aerial advertising. Every day you wait is a day they could. The first brand to own the sky in your category wins.',
        },
        {
          title: 'EXCLUSIVITY BY DESIGN',
          description:
            'We work with a limited number of clients per quarter to ensure every campaign receives our full attention and resources. Spots are limited.',
        },
        {
          title: 'MARKET MOMENTUM',
          description:
            "Egypt's events, real estate, and FMCG sectors are growing rapidly. The audience has never been larger, more connected, or more receptive to bold brand experiences.",
        },
      ],
    },
    cta: {
      eyebrow: 'READY TO',
      title: 'FLY HIGH?',
      subtitle: 'When The Trust is a Must',
      button: 'SCHEDULE A FREE CONSULTATION SESSION',
    },
    footer: {
      description:
        "Egypt's Premier Aerial Advertising & Media Solutions Company. We combine aviation expertise with creative media to make your brand impossible to ignore.",
      servicesTitle: 'SERVICES',
      companyTitle: 'COMPANY',
      servicesLinks: [
        { label: 'Aerial Advertising', path: '/aerial-advertising' },
        { label: 'Marketing & Digital', path: '/digital-marketing' },
        { label: 'Media Production', path: '/media-production' },
        { label: 'Web & App Development', path: '/web-development' },
      ],
      companyLinks: [
        { label: 'Contact Details', path: '/contact' },
        { label: 'About Us', path: '/about' },
        { label: 'News', path: '/news' },
        { label: 'Flight Safety', path: '/contact#safety' },
        { label: 'Privacy Policy', path: '/contact#privacy' },
        { label: 'Terms of Service', path: '/contact#terms' },
      ],
      copyright: '© 2024 FlyHigh Brothers. All altitude records reserved. Engineered for the Sky.',
    },
    contact: {
      title: "LET'S TALK",
      subtitle: 'Ready to take your brand to new heights? Reach out and let\'s start the conversation.',
      email: 'info@flyhigh.com.eg',
      phone: '+20 100 150 4356',
      company: 'Fly High Aviation Media Solutions LLC',
      instagram: '@flyhighbrothers_',
    },
    aboutPage: {
      title: 'ABOUT US',
      eyebrow: 'WHO WE ARE',
      subtitle: "Egypt's Premier Aerial Advertising & Media Solutions Company",
      intro:
        "FlyHigh Brothers was founded by aviation professionals who believed that Egypt's brands deserved more than billboards and banners. We combined deep expertise in aviation operations with a passion for marketing to create something entirely new.",
      quote: "We don't just run campaigns. We make history in the sky.",
    },
    newsPage: {
      title: 'NEWS',
      eyebrow: 'LATEST UPDATES',
      subtitle: 'Campaigns, activations, and milestones from FlyHigh Brothers',
      searchPlaceholder: 'Search news...',
      noResults: 'No news posts match your search.',
    },
    aerial: {
      eyebrow: 'ADVERTISING AT ALTITUDE',
      title: 'TURN THE SKY INTO A',
      titleAccent: 'BILLBOARD',
      description:
        "Our aerial advertising division operates Egypt's only dedicated commercial helicopter fleet for brand campaigns. Each activation is executed with military-grade precision and cinematic production values.",
      stats: [
        { label: 'FLEET', value: 'Helicopter' },
        { label: 'ALTITUDE', value: '50–100m' },
        { label: 'COVERAGE', value: 'All Egypt' },
        { label: 'REACH', value: '100K+', highlight: true },
      ],
      servicesTitle: 'OUR AERIAL SERVICES',
      servicesSubtitle:
        'A complete ecosystem of aerial solutions — designed to make your brand impossible to ignore.',
      services: [
        {
          title: 'Banner Dragging',
          description:
            'Our banner dragging service deploys large-format aerial banners trailing behind our helicopter at low altitude over your target audience.',
          specs: ['300m × 15m Banner Size', 'Up to 3km Visibility', 'Custom Duration'],
          idealFor: ['Stadiums & sports events', 'Beach & North Coast activations', 'City center campaigns', 'Concert & festival sponsorships'],
          large: true,
        },
        {
          title: 'Gift Dropping',
          description:
            'Deploy branded packages, promotional items, or surprise gifts from our helicopter directly to your crowd — generating organic social media content.',
          highlight: '10x MORE ORGANIC CONTENT',
          useCases: ['Product launches', 'Eid & national celebrations', 'Brand anniversaries', 'Fan engagement events'],
        },
        {
          title: 'Event Flyovers',
          description:
            'Precision helicopter passes above your venue — concerts, sports events, corporate launches, or beach festivals — creating a spectacle that elevates the entire experience.',
          idealFor: ['Music festivals & concerts', 'Sports events & stadiums', 'Corporate activations & launches', 'North Coast beach events'],
        },
        {
          title: 'VIP Sightseeing',
          description:
            'Premium aerial tours over Cairo, the Giza Pyramids, the Nile, and Egypt\'s most iconic landmarks.',
          tourOptions: ['Corporate hospitality packages', 'Tourism partnerships', 'VIP guest experiences', 'Aerial media content flights'],
        },
      ],
    },
    digital: {
      eyebrow: 'MARKETING & DIGITAL',
      title: 'YOUR BRAND,',
      titleAccent: 'EVERYWHERE',
      description: 'Full-spectrum digital and offline marketing — from strategy to execution.',
      strategyTitle: 'STRATEGY THAT CONVERTS',
      strategyDescription:
        'Our marketing division delivers end-to-end digital and offline campaigns — from brand strategy and creative direction to paid media, social media management, and performance analytics.',
      digital: ['Social Media Management', 'Content Creation', 'Paid Advertising', 'SEO & SEM'],
      strategy: ['Brand Strategy & Creative Direction', 'Influencer Marketing', 'Campaign Analytics & Reporting', 'Offline & OOH Campaigns'],
    },
    production: {
      eyebrow: 'MEDIA PRODUCTION',
      title: 'EVERY CAMPAIGN DESERVES',
      titleAccent: 'A CINEMATIC STORY',
      description:
        'Great advertising begins with great content. FlyHigh Brothers operates a full-service media production capability — from short-form social reels to broadcast-quality TV commercials, at every scale.',
      integration:
        'Our production team works in seamless integration with our aerial and marketing divisions. The footage captured during your aerial activation becomes the raw material for your next campaign.',
      capabilities: [
        {
          title: 'AERIAL CINEMATOGRAPHY',
          description: 'Cinema-grade footage captured from our helicopter fleet. Sweeping wide shots, dramatic tracking shots, and intimate close-ups.',
          features: ['4K/6K cinema camera rigs', 'Gyro-stabilised mounts', 'Integrated with aerial activations'],
        },
        {
          title: 'BRAND FILMS & COMMERCIALS',
          description: 'From 30-second broadcast spots to long-form brand documentaries.',
          features: ['TV & digital commercials', 'Corporate brand films', 'Product launch videos'],
        },
        {
          title: 'EVENT COVERAGE & ACTIVATION DOCS',
          description: 'Every activation deserves to live beyond the day itself.',
          features: ['Live event documentation', 'Activation recap reels', 'Multi-camera setups'],
        },
        {
          title: 'SHORT-FORM & SOCIAL CONTENT',
          description: 'Platform-native short-form content optimised for engagement and reach.',
          features: ['Instagram & TikTok Reels', 'YouTube Shorts', 'Motion graphics & captions'],
        },
      ],
      workflow: {
        title: 'FROM SHOOT TO SCREEN IN 4 STAGES',
        stages: [
          { number: '01', title: 'PRE-PRODUCTION', items: ['Creative brief & concept', 'Script & storyboard', 'Location scouting', 'Casting & crew assembly'] },
          { number: '02', title: 'PRODUCTION', items: ['Multi-camera shoot', 'Aerial footage capture', 'On-location direction', 'Sound recording'] },
          { number: '03', title: 'POST-PRODUCTION', items: ['Professional editing', 'Colour grading', 'Motion graphics & VFX', 'Sound design & mix'] },
          { number: '04', title: 'AMPLIFICATION', items: ['Platform-optimised exports', 'Social media cut-downs', 'Paid media assets', 'Performance reporting'] },
        ],
      },
    },
    web: {
      eyebrow: 'WEB & APP DEVELOPMENT',
      title: 'YOUR DIGITAL PRESENCE,',
      titleAccent: 'ELEVATED',
      description:
        'A brand that commands the sky deserves a digital presence that commands the screen. Our development team builds premium websites and mobile applications.',
      deliverables: [
        'Corporate websites & landing pages',
        'E-commerce platforms',
        'Mobile applications (iOS & Android)',
        'CMS integration & ongoing maintenance',
        'UI/UX design & branding',
      ],
    },
    story: {
      eyebrow: 'OUR STORY',
      title: 'WHERE AVIATION',
      titleAccent: 'MEETS CREATIVITY',
      quote: "We don't just run campaigns. We make history in the sky.",
      paragraphs: [
        "FlyHigh Brothers was founded by aviation professionals who believed that Egypt's brands deserved more than billboards and banners.",
        'We combined deep expertise in aviation operations with a passion for marketing to create something entirely new — Egypt\'s first and only dedicated aerial advertising and media solutions company.',
        'Today, we operate a helicopter fleet, a full-service digital marketing division, a media production studio, and a web development team. Everything under one roof.',
      ],
      vision: {
        title: 'VISION',
        heading: "ELEVATING EGYPT'S MOST AMBITIOUS BRANDS",
        description:
          "To pioneer a new dimension of advertising where brands transcend the noise—owning the sky and commanding every screen. Egypt's only aerial advertising company with a full-service media ecosystem.",
      },
      mission: {
        title: 'MISSION',
        heading: 'PRECISION EXECUTION. UNMATCHED IMPACT.',
        description:
          'To engineer unforgettable aerial and digital campaigns by combining aviation expertise, creative strategy, and end-to-end execution under one roof. Every campaign. Every client. Every time.',
      },
      ceo: {
        eyebrow: 'MEET OUR CEO',
        name: 'Captain Ahmed Abo El Magd',
        paragraphs: [
          'Captain Ahmed Abo El Magd is an aviation executive with more than 25 years of experience in aviation operations, aviation management, government relations, and business development.',
          'Upon this background, Fly High Brothers delivers innovative aviation services, aerial media solutions, and high-impact marketing campaigns that combine operational excellence with creativity and international standards.',
          'Fly High Brothers has pioneered unique aerial campaigns in Egypt, including helicopter advertising, aerial filming, parachute activation campaigns, and aviation-based marketing experiences that generated more than 100 million views across digital and media platforms.',
        ],
      },
      droneTech: {
        eyebrow: 'STRATEGIC PARTNERSHIP',
        title: 'FLYING WITH',
        titleAccent: 'DRONE TECH',
        subtitle:
          'FlyHigh Brothers partners with Drone Tech on major national and international projects — where precision aviation meets digital transformation at scale.',
        description:
          'Drone Tech is an Egyptian drone service provider delivering end-to-end aerial solutions. Beyond photography, they enable businesses and governments with Digital Twins powered by cutting-edge drone technology — accelerating digital transformation across AEC, smart cities, energy, mining, utilities, and agriculture.',
        quote: "We don't just provide drone services. We are partners in the Digital Transformation journey.",
        highlights: [
          'Major national and international project collaborations',
          'Digital Twins & actionable aerial intelligence',
          'Plan–design–build–operate asset lifecycle support',
          'Strategic partners in the emerging low-altitude economy',
        ],
        cta: 'Visit Drone Tech',
        url: '#',
        badge: 'Key Aviation & Drone Technology Partner',
      },
      partner: {
        eyebrow: 'OUR PARTNERS',
        title: 'POWERED BY SPARK',
        description:
          "FlyHigh Brothers is proud to partner with Spark — Egypt's leading outdoor media, LED screen, and large-format production company.",
        quote: 'Sky above. Ground below. Total brand domination.',
        credentials: [
          'LED screen supply & installation across Egypt and KSA',
          'Awarded tenders with Majid Al Futtaim (UAE/KSA)',
          'Exhibition presence at Cairo ICT, Sodic, and major events',
          'Large-format signage and outdoor media execution',
        ],
      },
      campaigns: {
        eyebrow: 'OUR WORK',
        title: 'CAMPAIGNS THAT',
        titleAccent: 'MADE HISTORY',
        subtitle: 'From Eid activations over Cairo to banner campaigns above 80,000-seat stadiums.',
        items: [
          { title: 'Eid Activation', location: 'Cairo, 2026' },
          { title: 'Stadium Banner', location: '80,000 Capacity' },
          { title: 'Brand Launch', location: 'North Coast' },
          { title: 'VIP Sightseeing', location: 'Giza Pyramids' },
        ],
      },
    },
  },
  ar: {
    brand: 'FlyHigh Brothers',
    tagline: 'الشركة الرائدة في مصر للإعلان الجوي وحلول الإعلام',
    nav: {
      home: 'الرئيسية',
      about: 'من نحن',
      aerial: 'الإعلان الجوي',
      digital: 'التسويق الرقمي',
      production: 'الإنتاج الإعلامي',
      web: 'تطوير الويب',
      story: 'قصتنا',
      news: 'الأخبار',
      contact: 'تواصل معنا',
    },
    hero: {
      eyebrow: 'حلول الإعلام الجوي الأولى في مصر',
      titleLine1: 'حيث العلامات',
      titleLine2: 'تحلّق عالياً',
      subtitle:
        'امتلك السماء. امتلك الشاشة. اختبر بُعداً جديداً من الإعلان حيث رسالتك مستحيلة التجاهل.',
      ctaPrimary: 'استكشف الحلول الجوية',
      ctaSecondary: 'شاهد العرض التقديمي',
    },
    problem: {
      eyebrow: 'التحدي',
      title: 'جمهورك يغرق في الإعلانات',
      subtitle: 'وتوقف عن الاستماع',
      description:
        'يتعرض المستهلك المصري لأكثر من 5,000 رسالة إعلانية يومياً. معظمها يُتجاهل. يُتخطى. يُحجب. يُنسى.',
      cards: [
        {
          title: 'عمى الإعلانات',
          description:
            'يتم تجاهل الإعلانات الرقمية بمعدلات قياسية. معدلات النقر انخفضت إلى أقل من 0.1%. ميزانيتك تختفي في الفراغ.',
        },
        {
          title: 'تشبع الإعلانات الخارجية',
          description:
            'كل لوحة إعلانية، كل جدار، كل حافلة محجوزة. علامتك تندمج مع الضوضاء بدلاً من التميز.',
        },
        {
          title: 'صفر قابلية للتذكر',
          description:
            'عندما يصرخ الجميع بنفس الطريقة، لا أحد يُسمع. العلامات تحتاج بُعداً جديداً — لا يمكن تكراره أو تجاهله.',
        },
      ],
      bridge: 'يبقى وسيط واحد فقط لا يمكن حجبه أو تخطيه أو تجاهله ←',
    },
    solution: {
      eyebrow: 'الحل',
      title: 'السماء هي الوسيط',
      titleAccent: 'الأخير غير المزدحم',
      description:
        'عندما تظهر علامتك في السماء فوق حشد من 50,000 شخص، لا يوجد زر تمرير. لا حاجب إعلانات. لا رسالة منافسة. فقط علامتك — على نطاق واسع، في حركة، مستحيلة التفويت.',
      stats: [
        { value: '100%', label: 'قابلية الرؤية' },
        { value: '0', label: 'حاجبات إعلانات' },
        { value: '50K+', label: 'انطباعات متزامنة' },
      ],
    },
    whyUs: {
      eyebrow: 'لماذا نحن',
      title: 'فرق FlyHigh Brothers',
      items: [
        {
          title: 'وصول حصري',
          description: 'نشغّل أسطول الإعلان الجوي الوحيد المخصص في مصر. لا وكالة أخرى تقدم ما نقدمه.',
        },
        {
          title: 'رؤية لا مثيل لها',
          description: 'التعرض على مستوى السماء يصل لعشرات الآلاف في آن واحد — بدون حجب إعلانات و100% قابلية رؤية.',
        },
        {
          title: 'تموضع متميز',
          description: 'خدماتنا مصممة للعلامات التي تطلب أعلى مستوى من التنفيذ والعرض والنتائج.',
        },
        {
          title: 'منظومة متكاملة',
          description: 'من السماء إلى الشاشة، نتولى الجوي والرقمي والإنتاج والتطوير — تحت سقف واحد.',
        },
      ],
    },
    services: {
      eyebrow: 'ماذا نفعل',
      title: 'امتلك السماء',
      titleAccent: 'امتلك الشاشة',
      subtitle: 'منظومة متكاملة من الحلول الجوية والإعلامية — مصممة لجعل علامتك مستحيلة التجاهل.',
      items: [
        { title: 'الإعلان الجوي', path: '/aerial-advertising' },
        { title: 'التسويق الرقمي', path: '/digital-marketing' },
        { title: 'الإنتاج الإعلامي', path: '/media-production' },
        { title: 'تطوير الويب والتطبيقات', path: '/web-development' },
      ],
    },
    trustedBy: {
      title: 'موثوق به من علامات تحلّق معنا',
      industries: ['العقارات', 'الأغذية والمشروبات', 'التكنولوجيا', 'الأزياء', 'الفعاليات', 'القطاع الحكومي'],
    },
    flyPartners: {
      eyebrow: 'موثوق به من',
      title: 'علامات تحلّق معنا',
      description:
        'نفخر بشراكتنا مع بعض أكثر العلامات احتراماً في مصر عبر العقارات والتكنولوجيا والأغذية والمشروبات والأزياء وغيرها.',
    },
    commercialPartners: {
      eyebrow: 'الإعلانات التجارية',
      title: 'علامات تثق بعدستنا',
      subtitle: 'سجل الإنتاج الإعلاني التجاري',
      description:
        'أنتج منتجنا أحمد إبراهيم حملات إعلانية لبعض أكثر العلامات شهرة في مصر والعالم العربي — محتوى إعلاني عالي التأثير بجودة البث.',
    },
    impact: {
      eyebrow: 'أثرنا',
      title: 'أرقام تتحدث',
      stats: [
        { value: '50+', label: 'حملة ناجحة' },
        { value: '100M+', label: 'انطباعات للجمهور' },
        { value: '20+', label: 'علامة تجارية' },
        { value: '100%', label: 'معدل إتمام الحملات' },
      ],
    },
    testimonials: {
      eyebrow: 'شهادات العملاء',
      title: 'ماذا يقول عملاؤنا',
      items: [
        {
          quote: 'حملة اللافتة الجوية ولّدت ضجة أكبر من إنفاقنا الرقمي بالكامل ذلك الشهر.',
          role: 'مدير التسويق',
          company: 'مطور عقاري',
        },
        {
          quote: 'قدّم FlyHigh Brothers تفعيل إسقاط هدايا لا يزال جمهورنا يتحدث عنه بعد ستة أشهر.',
          role: 'مدير العلامة',
          company: 'شركة سلع استهلاكية',
        },
        {
          quote: 'فريق الإنتاج التقط قصة علامتنا بطريقة لم نتخيلها. اللقطات الجوية كانت مذهلة.',
          role: 'الرئيس التنفيذي',
          company: 'علامة أزياء وتجزئة',
        },
        {
          quote: 'مزيج الإعلان الجوي والتضخيم الرقمي أعطانا وصولاً لم نحققه عبر القنوات التقليدية.',
          role: 'رئيس التسويق',
          company: 'شركة تكنولوجيا',
        },
      ],
    },
    howWeWork: {
      eyebrow: 'كيف نعمل',
      title: 'من الموجز إلى الإطلاق',
      steps: [
        { number: '01', title: 'الموجز', description: 'نبدأ بفهم عميق لعلامتك وجمهورك وأهداف حملتك.' },
        { number: '02', title: 'الاستراتيجية', description: 'نطور مفهوم حملة مخصص مع خطة تنفيذ وجدول زمني ومؤشرات أداء.' },
        { number: '03', title: 'التنفيذ', description: 'ننشر حملتك بدقة — في الوقت المحدد وضمن الميزانية وبأعلى جودة.' },
        { number: '04', title: 'النتائج', description: 'نقدم تقارير الأداء ونشارك الرؤى ونحسّن لحملتك القادمة.' },
      ],
    },
    opportunity: {
      eyebrow: 'الفرصة',
      title: 'النافذة مفتوحة',
      titleAccent: 'الآن',
      items: [
        { title: 'ميزة السباق', description: 'منافسوك لم يكتشفوا الإعلان الجوي بعد. أول علامة تمتلك السماء في فئتها تفوز.' },
        { title: 'حصرية بالتصميم', description: 'نعمل مع عدد محدود من العملاء كل ربع سنة. الأماكن محدودة.' },
        { title: 'زخم السوق', description: 'قطاعات الفعاليات والعقارات والسلع الاستهلاكية في مصر تنمو بسرعة.' },
      ],
    },
    cta: {
      eyebrow: 'مستعد',
      title: 'للتحليق؟',
      subtitle: 'عندما يكون الثقة ضرورة',
      button: 'احجز جلسة استشارة مجانية',
    },
    footer: {
      description: 'الشركة الرائدة في مصر للإعلان الجوي وحلول الإعلام. نجمع خبرة الطيران مع الإبداع الإعلامي.',
      servicesTitle: 'الخدمات',
      companyTitle: 'الشركة',
      servicesLinks: [
        { label: 'الإعلان الجوي', path: '/aerial-advertising' },
        { label: 'التسويق الرقمي', path: '/digital-marketing' },
        { label: 'الإنتاج الإعلامي', path: '/media-production' },
        { label: 'تطوير الويب', path: '/web-development' },
      ],
      companyLinks: [
        { label: 'بيانات التواصل', path: '/contact' },
        { label: 'من نحن', path: '/about' },
        { label: 'الأخبار', path: '/news' },
        { label: 'سلامة الطيران', path: '/contact#safety' },
        { label: 'سياسة الخصوصية', path: '/contact#privacy' },
        { label: 'شروط الخدمة', path: '/contact#terms' },
      ],
      copyright: '© 2024 FlyHigh Brothers. جميع أرقام الارتفاع محفوظة. مصمم للسماء.',
    },
    contact: {
      title: 'لنتحدث',
      subtitle: 'مستعد لرفع علامتك إلى آفاق جديدة؟ تواصل معنا وابدأ المحادثة.',
      email: 'info@flyhigh.com.eg',
      phone: '+20 100 150 4356',
      company: 'Fly High Aviation Media Solutions LLC',
      instagram: '@flyhighbrothers_',
    },
    aboutPage: {
      title: 'من نحن',
      eyebrow: 'تعرف علينا',
      subtitle: 'الشركة الرائدة في مصر للإعلان الجوي وحلول الإعلام',
      intro:
        'تأسست FlyHigh Brothers على يد محترفين في الطيران آمنوا أن علامات مصر تستحق أكثر من اللوحات الإعلانية. جمعنا خبرة عميقة في عمليات الطيران مع شغف التسويق لنبتكر شيئاً جديداً.',
      quote: 'لا ندير حملات فحسب. نصنع التاريخ في السماء.',
    },
    newsPage: {
      title: 'الأخبار',
      eyebrow: 'آخر المستجدات',
      subtitle: 'حملات وتفعيلات وإنجازات من FlyHigh Brothers',
      searchPlaceholder: 'ابحث في الأخبار...',
      noResults: 'لا توجد أخبار مطابقة لبحثك.',
    },
    aerial: {
      eyebrow: 'الإعلان على ارتفاع',
      title: 'حوّل السماء إلى',
      titleAccent: 'لوحة إعلانية',
      description: 'قسم الإعلان الجوي يشغّل أسطول الهليكوبتر التجاري الوحيد المخصص للحملات في مصر.',
      stats: [
        { label: 'الأسطول', value: 'هليكوبتر' },
        { label: 'الارتفاع', value: '50–100م' },
        { label: 'التغطية', value: 'كل مصر' },
        { label: 'الوصول', value: '100K+', highlight: true },
      ],
      servicesTitle: 'خدماتنا الجوية',
      servicesSubtitle: 'منظومة متكاملة من الحلول الجوية — مصممة لجعل علامتك مستحيلة التجاهل.',
      services: [
        {
          title: 'سحب اللافتات',
          description: 'ننشر لافتات جوية كبيرة خلف الهليكوبتر على ارتفاع منخفض فوق جمهورك المستهدف.',
          specs: ['300م × 15م حجم اللافتة', 'رؤية حتى 3كم', 'مدة مخصصة'],
          idealFor: ['الملاعب والفعاليات الرياضية', 'تفعيلات الشاطئ والساحل الشمالي', 'حملات وسط المدينة', 'رعاية الحفلات والمهرجانات'],
          large: true,
        },
        {
          title: 'إسقاط الهدايا',
          description: 'ننشر حزماً علامية أو هدايا مفاجئة من الهليكوبتر مباشرة إلى الحشد.',
          highlight: '10x محتوى عضوي أكثر',
          useCases: ['إطلاق المنتجات', 'احتفالات العيد والمناسبات الوطنية', 'ذكرى تأسيس العلامة', 'فعاليات تفاعل الجماهير'],
        },
        {
          title: 'تحليق الفعاليات',
          description: 'تحليقات دقيقة فوق مكان فعاليتك — حفلات، رياضة، إطلاقات شركات، أو مهرجانات شاطئية.',
          idealFor: ['مهرجانات الموسيقى والحفلات', 'الفعاليات الرياضية والملاعب', 'تفعيلات وإطلاقات الشركات', 'فعاليات الساحل الشمالي'],
        },
        {
          title: 'جولات VIP',
          description: 'جولات جوية متميزة فوق القاهرة وأهرامات الجيزة والنيل وأشهر معالم مصر.',
          tourOptions: ['باقات الضيافة الشركات', 'شراكات سياحية', 'تجارب ضيوف VIP', 'رحلات تصوير إعلامي جوي'],
        },
      ],
    },
    digital: {
      eyebrow: 'التسويق الرقمي',
      title: 'علامتك،',
      titleAccent: 'في كل مكان',
      description: 'تسويق رقمي وخارجي متكامل — من الاستراتيجية إلى التنفيذ.',
      strategyTitle: 'استراتيجية تحوّل',
      strategyDescription: 'قسم التسويق يقدم حملات رقمية وخارجية متكاملة — من استراتيجية العلامة إلى الإعلانات المدفوعة وإدارة السوشيال ميديا.',
      digital: ['إدارة السوشيال ميديا', 'إنشاء المحتوى', 'الإعلانات المدفوعة', 'SEO & SEM'],
      strategy: ['استراتيجية العلامة والإخراج الإبداعي', 'تسويق المؤثرين', 'تحليلات الحملات والتقارير', 'حملات خارجية وOOH'],
    },
    production: {
      eyebrow: 'الإنتاج الإعلامي',
      title: 'كل حملة تستحق',
      titleAccent: 'قصة سينمائية',
      description: 'الإعلان العظيم يبدأ بمحتوى عظيم. نقدم إنتاجاً إعلامياً متكاملاً — من الريلز القصيرة إلى إعلانات تلفزيونية بجودة البث.',
      integration: 'فريق الإنتاج يعمل بتكامل مع أقسام الجوي والتسويق. اللقطات الملتقطة أثناء التفعيل الجوي تصبح مادة حملتك القادمة.',
      capabilities: [
        { title: 'التصوير السينمائي الجوي', description: 'لقطات بجودة السينما من أسطول الهليكوبتر.', features: ['كاميرات 4K/6K', 'حوامل مثبتة', 'تكامل مع التفعيلات الجوية'] },
        { title: 'أفلام العلامة والإعلانات', description: 'من إعلانات 30 ثانية إلى أفلام وثائقية للعلامة.', features: ['إعلانات تلفزيونية ورقمية', 'أفلام شركات', 'فيديوهات إطلاق المنتجات'] },
        { title: 'تغطية الفعاليات', description: 'كل تفعيل يستحق أن يعيش بعد يومه.', features: ['توثيق مباشر', 'ريلز ملخصات', 'إعدادات متعددة الكاميرات'] },
        { title: 'محتوى قصير وسوشيال', description: 'محتوى قصير أصلي للمنصات محسّن للتفاعل.', features: ['ريلز إنستغرام وتيك توك', 'يوتيوب شورتس', 'موشن جرافيك'] },
      ],
      workflow: {
        title: 'من التصوير إلى الشاشة في 4 مراحل',
        stages: [
          { number: '01', title: 'ما قبل الإنتاج', items: ['الموجز والمفهوم', 'السيناريو والستوري بورد', 'اختيار المواقع', 'تجميع الطاقم'] },
          { number: '02', title: 'الإنتاج', items: ['تصوير متعدد الكاميرات', 'لقطات جوية', 'إخراج في الموقع', 'تسجيل الصوت'] },
          { number: '03', title: 'ما بعد الإنتاج', items: ['مونتاج احترافي', 'تصحيح ألوان', 'موشن جرافيك ومؤثرات', 'تصميم صوت'] },
          { number: '04', title: 'التضخيم', items: ['تصدير للمنصات', 'نسخ للسوشيال', 'أصول إعلانات مدفوعة', 'تقارير الأداء'] },
        ],
      },
    },
    web: {
      eyebrow: 'تطوير الويب والتطبيقات',
      title: 'حضورك الرقمي،',
      titleAccent: 'مرتفع',
      description: 'علامة تسيطر على السماء تستحق حضوراً رقمياً يسيطر على الشاشة.',
      deliverables: [
        'مواقع شركات وصفحات هبوط',
        'منصات تجارة إلكترونية',
        'تطبيقات موبايل (iOS و Android)',
        'تكامل CMS وصيانة مستمرة',
        'تصميم UI/UX والهوية',
      ],
    },
    story: {
      eyebrow: 'قصتنا',
      title: 'حيث يلتقي الطيران',
      titleAccent: 'بالإبداع',
      quote: 'لا ندير حملات فحسب. نصنع التاريخ في السماء.',
      paragraphs: [
        'تأسست FlyHigh Brothers على يد محترفين في الطيران آمنوا أن علامات مصر تستحق أكثر من اللوحات الإعلانية.',
        'جمعنا خبرة عميقة في عمليات الطيران مع شغف التسويق لنبتكر شيئاً جديداً — أول شركة إعلان جوي وحلول إعلامية مخصصة في مصر.',
        'اليوم نشغّل أسطول هليكوبتر، قسم تسويق رقمي، استوديو إنتاج إعلامي، وفريق تطوير ويب. كل شيء تحت سقف واحد.',
      ],
      vision: {
        title: 'الرؤية',
        heading: 'رفع أطول علامات مصر طموحاً',
        description: 'ريادة بُعد جديد من الإعلان حيث تتجاوز العلامات الضوضاء — تمتلك السماء وتسيطر على كل شاشة.',
      },
      mission: {
        title: 'المهمة',
        heading: 'تنفيذ دقيق. أثر لا مثيل له.',
        description: 'هندسة حملات جوية ورقمية لا تُنسى بدمج خبرة الطيران والاستراتيجية الإبداعية والتنفيذ المتكامل.',
      },
      ceo: {
        eyebrow: 'تعرف على الرئيس التنفيذي',
        name: 'الكابتن أحمد أبو المجد',
        paragraphs: [
          'الكابتن أحمد أبو المجد تنفيذي في مجال الطيران بخبرة تتجاوز خمسة وعشرين عاماً في عمليات الطيران، وإدارة الطيران، والعلاقات الحكومية، وتطوير الأعمال.',
          'وانطلاقاً من هذه الخلفية، تقدّم Fly High Brothers خدمات طيران مبتكرة، وحلول إعلام جوي، وحملات تسويقية عالية الأثر تجمع بين التميّز التشغيلي والإبداع والمعايير الدولية.',
          'وقد نفّذت Fly High Brothers حملات جوية رائدة في مصر، شملت الإعلان بالطائرات المروحية، والتصوير الجوي، وحملات التفعيل بالمظلات، والتجارب التسويقية المستندة إلى الطيران، والتي حققت أكثر من 100 مليون مشاهدة عبر المنصات الرقمية والإعلامية.',
        ],
      },
      droneTech: {
        eyebrow: 'شراكة استراتيجية',
        title: 'نحلّق مع',
        titleAccent: 'DRONE TECH',
        subtitle:
          'تتعاون FlyHigh Brothers مع Drone Tech في مشاريع وطنية ودولية كبرى — حيث يلتقي الطيران الدقيق بالتحوّل الرقمي على نطاق واسع.',
        description:
          'Drone Tech مزوّد مصري لخدمات الطائرات بدون طيار تقدّم حلولاً جوية متكاملة. إلى جانب التصوير، تمكّن الشركات والحكومات من بناء التوائم الرقمية بتقنية الطائرات المتقدمة — لتسريع التحوّل الرقمي في مجالات الهندسة والبناء، والمدن الذكية، والطاقة، والتعدين، والمرافق، والزراعة.',
        quote: 'لا نقدّم خدمات طائرات بدون طيار فحسب. نحن شركاء في رحلة التحوّل الرقمي.',
        highlights: [
          'تعاون في مشاريع وطنية ودولية كبرى وحساسة',
          'توائم رقمية وذكاء جوي قابل للتنفيذ',
          'دعم دورة حياة الأصول: تخطيط–تصميم–بناء–تشغيل',
          'شركاء استراتيجيون في اقتصاد الارتفاعات المنخفضة الناشئ',
        ],
        cta: 'زيارة Drone Tech',
        url: '#',
        badge: 'الشريك الرئيسي في قطاع الطيران وتكنولوجيا الطائرات بدون طيار',
      },
      partner: {
        eyebrow: 'شركاؤنا',
        title: 'مدعومون من SPARK',
        description: 'نفتخر بشراكتنا مع Spark — الشركة الرائدة في الإعلام الخارجي وشاشات LED في مصر.',
        quote: 'السماء فوق. الأرض تحت. هيمنة علامة كاملة.',
        credentials: [
          'توريد وتركيب شاشات LED في مصر والسعودية',
          'مناقصات مع ماجد الفطيم (الإمارات/السعودية)',
          'حضور في Cairo ICT وSodic والفعاليات الكبرى',
          'تنفيذ لافتات كبيرة وإعلام خارجي',
        ],
      },
      campaigns: {
        eyebrow: 'أعمالنا',
        title: 'حملات صنعت',
        titleAccent: 'التاريخ',
        subtitle: 'من تفعيلات العيد فوق القاهرة إلى حملات لافتات فوق ملاعب 80,000 مقعد.',
        items: [
          { title: 'تفعيل العيد', location: 'القاهرة، 2026' },
          { title: 'لافتة الملعب', location: '80,000 مقعد' },
          { title: 'إطلاق علامة', location: 'الساحل الشمالي' },
          { title: 'جولة VIP', location: 'أهرامات الجيزة' },
        ],
      },
    },
  },
};

export const serviceImageKeys = {
  'Aerial Advertising': 'serviceAerial',
  'الإعلان الجوي': 'serviceAerial',
  'Marketing & Digital': 'serviceDigital',
  'التسويق الرقمي': 'serviceDigital',
  'Media Production': 'serviceProduction',
  'الإنتاج الإعلامي': 'serviceProduction',
  'Web & App Development': 'serviceWeb',
  'تطوير الويب والتطبيقات': 'serviceWeb',
};
